/**
 * "I accept the store terms" checkbox in the cart (snippets/kyn-terms.liquid).
 *
 * Blocks the checkout buttons (cart page, drawer) until it is ticked. The choice is saved as the cart
 * attribute "Akceptacja regulaminu", so it survives cart re-renders and is copied to the order.
 * The cart page and the drawer can both hold a checkbox, so the state lives in the module.
 */

const ATTR = 'Akceptacja regulaminu';

let accepted = false;
let initialised = false;

/** @type {Promise<unknown> | null} */
let saving = null;

/** @type {Set<KynTerms>} */
const boxes = new Set();

function save() {
  const request = fetch(`${Theme.routes.cart_update_url}.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ attributes: { [ATTR]: accepted ? new Date().toISOString() : '' } }),
  })
    .catch((error) => console.error(error))
    .finally(() => {
      if (saving === request) saving = null;
    });
  saving = request;
}

/** Blocks the checkout buttons until the terms are accepted. */
function guard(event) {
  if (!boxes.size || (accepted && !saving)) return;
  const target = /** @type {HTMLElement} */ (event.target);
  const isCheckout =
    event.type === 'submit'
      ? event.submitter?.getAttribute('name') === 'checkout'
      : target.closest?.('button[name="checkout"]');
  if (!isCheckout) return;

  // Accepted but the attribute is still being saved: wait, then press the same button again.
  if (accepted) {
    event.preventDefault();
    event.stopImmediatePropagation();
    const button = event.type === 'submit' ? event.submitter : target.closest('button[name="checkout"]');
    saving?.then(() => /** @type {HTMLElement | null} */ (button)?.click());
    return;
  }

  event.preventDefault();
  event.stopImmediatePropagation();
  const visible = [...boxes].find((box) => box.offsetParent) ?? [...boxes][0];
  visible?.showError();
}

class KynTerms extends HTMLElement {
  connectedCallback() {
    const input = this.#input();
    if (!initialised) {
      initialised = true;
      accepted = Boolean(input?.checked);
      document.addEventListener('click', guard, true);
      document.addEventListener('submit', guard, true);
    }
    boxes.add(this);
    if (input) input.checked = accepted;
    input?.addEventListener('change', this.#onChange);
  }

  disconnectedCallback() {
    boxes.delete(this);
    this.#input()?.removeEventListener('change', this.#onChange);
  }

  showError() {
    this.toggleAttribute('data-invalid', true);
    this.querySelector('[data-error]')?.removeAttribute('hidden');
    this.#input()?.focus();
  }

  #input() {
    return /** @type {HTMLInputElement | null} */ (this.querySelector('input[type="checkbox"]'));
  }

  #onChange = () => {
    accepted = Boolean(this.#input()?.checked);
    for (const box of boxes) {
      const input = box.#input();
      if (input) input.checked = accepted;
      if (accepted) {
        box.removeAttribute('data-invalid');
        box.querySelector('[data-error]')?.setAttribute('hidden', '');
      }
    }
    save();
  };
}

if (!customElements.get('kyn-terms')) customElements.define('kyn-terms', KynTerms);
